import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import { taxDeductionCalculationQueryPrefix } from "./useTaxDeductions";
import type {
  ApiPaymentRecord,
  CreatePaymentRecordPayload,
  UpdatePaymentRecordPayload,
} from "../session-cost-records";

export type {
  ApiPaymentRecord,
  CreatePaymentRecordPayload,
  UpdatePaymentRecordPayload,
} from "../session-cost-records";

// The non-versioned default OpenAPI document does not model an x-api-version
// parameter, so the version is pinned at runtime through the untyped `headers`
// request option — the same pattern useCycles/usePatient use.
const paymentApiVersionHeader = { "x-api-version": "2026-06-17" } as const;

export function paymentRecordsQueryKey(patientId: string | null) {
  return ["payment-records", patientId] as const;
}

export function usePaymentRecords() {
  const { client, patientId } = useApiClient();

  return useQuery<ApiPaymentRecord[]>({
    queryKey: paymentRecordsQueryKey(patientId),
    queryFn: async () => {
      if (!patientId) return [];
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/payment-records",
        {
          headers: paymentApiVersionHeader,
          params: { path: { patientId } },
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to load payment records",
          "usePaymentRecords",
          response?.status,
        );
      }
      return data?.data ?? [];
    },
    enabled: !!patientId,
    placeholderData: [],
  });
}

export function useCreatePaymentRecord() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreatePaymentRecordPayload) => {
      if (!patientId) throw new Error("No patient context");
      const { data, error, response } = await client.POST(
        "/patients/{patientId}/payment-records",
        {
          headers: paymentApiVersionHeader,
          params: { path: { patientId } },
          body: payload,
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to create payment record",
          "useCreatePaymentRecord",
          response?.status,
        );
      }
      if (!data?.data) throw new Error("Payment record response was empty");
      return data.data;
    },
    onSuccess: (record) => {
      if (!patientId) return;
      queryClient.setQueryData<ApiPaymentRecord[]>(
        paymentRecordsQueryKey(patientId),
        (current = []) => [
          record,
          ...current.filter((item) => item.id !== record.id),
        ],
      );
      void queryClient.invalidateQueries({
        queryKey: paymentRecordsQueryKey(patientId),
      });
      void queryClient.invalidateQueries({
        queryKey: taxDeductionCalculationQueryPrefix(patientId),
      });
    },
  });
}

export function useUpdatePaymentRecord() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      paymentRecordId,
      updates,
    }: {
      paymentRecordId: string;
      updates: UpdatePaymentRecordPayload;
    }) => {
      if (!patientId) throw new Error("No patient context");
      const { data, error, response } = await client.PATCH(
        "/patients/{patientId}/payment-records/{paymentRecordId}",
        {
          headers: paymentApiVersionHeader,
          params: {
            path: { patientId, paymentRecordId },
          },
          body: updates,
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to update payment record",
          "useUpdatePaymentRecord",
          response?.status,
        );
      }
      if (!data?.data) throw new Error("Payment record response was empty");
      return data.data;
    },
    onSuccess: (record) => {
      if (!patientId) return;
      queryClient.setQueryData<ApiPaymentRecord[]>(
        paymentRecordsQueryKey(patientId),
        (current = []) =>
          current.map((item) => (item.id === record.id ? record : item)),
      );
      void queryClient.invalidateQueries({
        queryKey: paymentRecordsQueryKey(patientId),
      });
      void queryClient.invalidateQueries({
        queryKey: taxDeductionCalculationQueryPrefix(patientId),
      });
    },
  });
}

export function useDeletePaymentRecord() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentRecordId: string) => {
      if (!patientId) throw new Error("No patient context");
      const { error, response } = await client.DELETE(
        "/patients/{patientId}/payment-records/{paymentRecordId}",
        {
          headers: paymentApiVersionHeader,
          params: {
            path: { patientId, paymentRecordId },
          },
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to delete payment record",
          "useDeletePaymentRecord",
          response?.status,
        );
      }
    },
    onSuccess: (_result, paymentRecordId) => {
      if (!patientId) return;
      queryClient.setQueryData<ApiPaymentRecord[]>(
        paymentRecordsQueryKey(patientId),
        (current = []) => current.filter((item) => item.id !== paymentRecordId),
      );
      void queryClient.invalidateQueries({
        queryKey: paymentRecordsQueryKey(patientId),
      });
      void queryClient.invalidateQueries({
        queryKey: taxDeductionCalculationQueryPrefix(patientId),
      });
    },
  });
}
